import React, {useState, useEffect, useRef, useContext} from 'react';
import {Button, Col, Form, Input, Modal, notification, PageHeader, Row, Space, Table} from "antd";
import {API} from "../api";
import {useHistory, useParams} from "react-router-dom";
import {handleError, setStickyDropdownSelection} from "../utils/util";

const EditableContext = React.createContext(null);

const EditableRow = ({index, ...props}) => {
    const [form] = Form.useForm();
    return (
        <Form form={form} component={false}>
            <EditableContext.Provider value={form}>
                <tr {...props} />
            </EditableContext.Provider>
        </Form>
    );
};

const EditableCell = ({
                          title,
                          editable,
                          children,
                          dataIndex,
                          record,
                          handleSave,
                          ...restProps
                      }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);
    const form = useContext(EditableContext);
    useEffect(() => {
        if (editing) {
            inputRef.current.focus();
        }
    }, [editing]);

    const toggleEdit = () => {
        setEditing(!editing);
        form.setFieldsValue({
            [dataIndex]: record[dataIndex],
        });
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            toggleEdit();
            handleSave({...record, ...values});
        } catch (errInfo) {
            console.log('Save failed:', errInfo);
        }
    };

    let childNode = children;

    if (editable) {
        childNode = editing ? (
            <Form.Item
                style={{margin: 0}}
                name={dataIndex}>
                <Input ref={inputRef} onPressEnter={save} onBlur={save}
                       data-cy={`${record.resourceType.toLowerCase()}-${record.namespace.toLowerCase()}-input`}/>
            </Form.Item>
        ) : (
            <div
                className="editable-cell-value-wrap"
                style={{}}
                data-cy={`${record.resourceType.toLowerCase()}-${record.namespace.toLowerCase()}`}
                onClick={toggleEdit}
            >
                {children}
            </div>
        );
    }

    return <td {...restProps}>{childNode}</td>;
};

export default function Resources(props) {
    const [templates, setTemplates] = useState([])
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [templateUrl, setTemplateURL] = useState(null);
    const [templateNamespace, setTemplateNamespace] = useState(null);

    let history = useHistory();
    let {client_id} = useParams();

    const columns = [
        {
            title: 'Resource Type',
            dataIndex: 'resourceType',
            key: 'resourceType',
            defaultSortOrder: 'ascend',
            sorter: (a, b) => a.resourceType.localeCompare(b.resourceType)
        },
        {
            title: 'Namespace',
            dataIndex: 'namespace',
            key: 'namespace',
            sorter: (a, b) => a.namespace.localeCompare(b.namespace)
        },
        {
            title: 'File (Data Source)',
            dataIndex: 'file',
            key: 'file',
            editable: true
        },
        {
            title: 'Action',
            key: 'action',
            render: (text, record) => (
                <Space size="middle">
                    <Button data-cy={`${record.resourceType.toLowerCase()}-${record.namespace.toLowerCase()}-import-btn`}
                            onClick={() => onFinish(record)}>
                        Import
                    </Button>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        let mounted = true;
        API.get("/mapping/templates").then(res => {
            if (mounted) {
                setTemplates(res.data.templates)
            }
        })
        return () => mounted = false;
    }, []);

    const onFinish = async (item) => {
        if (item.file === '') {
            notification.error({
                description: 'Please set file before importing'
            })
            return
        }

        API.post("/mapping/templates/import", null, {
            params: {
                client_id: client_id,
                resourcetype: item.resourceType,
                namespace: item.namespace,
                file: item.file
            }
        }).then(res => {
            if (res.data) {
                history.push({pathname: `/map/${client_id}`})
                setStickyDropdownSelection(client_id, {
                    resourcetype: item.resourceType,
                    namespace: item.namespace,
                    file: item.file
                })
            }
        }).catch(err => handleError(err))
    };

    const handleSave = (row) => {
        const newData = [...templates];
        const index = newData.findIndex((item) => row.resourceType === item.resourceType && row.namespace === item.namespace);
        const item = newData[index];
        newData.splice(index, 1, {...item, ...row});
        setTemplates(newData)
    };

    const components = {
        body: {
            row: EditableRow,
            cell: EditableCell,
        },
    };
    const _columns = columns.map((col) => {
        if (!col.editable) {
            return col;
        }

        return {
            ...col,
            onCell: (record) => ({
                record,
                editable: col.editable,
                dataIndex: col.dataIndex,
                title: col.title,
                handleSave: handleSave,
            }),
        };
    });

    const handleOk = () => {
        if (!templateUrl) {
            notification.error({description: "Error: URL cannot be empty"})
            return
        }
        if (!templateNamespace || templateNamespace.trim() === '') {
            notification.error({description: "Error: namespace cannot be empty"})
            return
        }
        if (!templateUrl.endsWith('.json')) {
            notification.error({description: "Error: URL must end with '.json'"})
            return
        }

        let body = {
            client_id: client_id,
            namespace: templateNamespace,
            url: templateUrl

        }
        API.post('/mapping/templates', body).then(res => {
            notification.success({
                message: `Created new template`
            })
            setIsModalVisible(false);
            API.get("/mapping/templates").then(res => {
                setTemplates(res.data.templates)
            })
        }).catch(err => {
            handleError(err)
            setIsModalVisible(false);
        })
    }

    const handleCancel = () => setIsModalVisible(false);

    return (
        <>
            <PageHeader
                className="site-page-header"
                onBack={() => window.history.back()}
                title="FHIR Resources"
                subTitle="Import valid FHIR resource templates to client for mapping"
            />
            <Row style={{marginBottom: 10}}>
                <Col flex={1} />
                <Col>
                    <span style={{color: "#1890ff", cursor: "pointer"}} onClick={() => setIsModalVisible(true)}>
                        Don't see the template you need?
                    </span>
                    <Modal title="Add a new FHIR Resource Template"
                           visible={isModalVisible}
                           width={900}
                           footer={[
                               <Button key="back" onClick={handleCancel}>
                                   Cancel
                               </Button>,
                               <Button key="submit" type="primary" loading={false} onClick={handleOk}>
                                   Import
                               </Button>,
                           ]}
                           onOk={handleOk}
                           onCancel={handleCancel}>
                        <Row gutter={[16, 16]} align="middle">
                            <Col span={14}>
                                <Row>
                                    <p>
                                        <b>New templates can be generated from example FHIR resources (in JSON) provided
                                            by HL7.</b>
                                    </p>
                                    <p>
                                        To generate a new template, please find a URL which resolves to an
                                        example FHIR resource provided by HL7. These URL's may be found
                                        on the <a rel="noreferrer" target="_blank"
                                                  href="http://hl7.org/fhir/us/carin-bb/STU1/StructureDefinition-C4BB-Patient.html">
                                        Structure Definition
                                    </a> pages for different FHIR resources.
                                    </p>
                                </Row>
                                <Row gutter={[16, 16]} style={{marginBottom: 10}}>
                                    <Input addonBefore="URL: "
                                           onChange={(e) => setTemplateURL(e.target.value)}
                                           placeholder="http://hl7.org/fhir/us/carin-bb/STU1/Patient-ExamplePatient1.json"/>
                                </Row>
                                <Row gutter={[16, 16]}>
                                    <Input addonBefore="Namespace: "
                                           onChange={(e) => setTemplateNamespace(e.target.value)}
                                           placeholder="e.g. BloodPressure"/>
                                </Row>
                            </Col>
                            <Col span={10} align="start" style={{paddingLeft: 25}}>
                                <h3>Examples</h3>
                                <h4 style={{marginBottom: 0}}>Structure Definitions: </h4>
                                <a rel="noreferrer" target="_blank"
                                   href="http://hl7.org/fhir/us/carin-bb/STU1/StructureDefinition-C4BB-Patient.html">
                                    Patient
                                </a>
                                <br/>
                                <a rel="noreferrer" target="_blank"
                                   href="http://hl7.org/fhir/us/carin-bb/STU1/StructureDefinition-C4BB-Coverage.html">
                                    Coverage
                                </a>
                                <br/>
                                <a rel="noreferrer" target="_blank"
                                   href="http://hl7.org/fhir/us/carin-bb/STU1/StructureDefinition-C4BB-ExplanationOfBenefit-Inpatient-Institutional.html">
                                    ExplanationOfBenefit Inpatient Institutional
                                </a>
                                <br/>
                                <a rel="noreferrer" target="_blank"
                                   href="https://www.hl7.org/fhir/condition.html">
                                    Condition
                                </a>
                                <br/><br/>
                                <h4 style={{marginBottom: 0}}>
                                    FHIR Resources:
                                </h4>
                                <a rel="noreferrer" target="_blank"
                                   href="http://hl7.org/fhir/us/carin-bb/STU1/Patient-ExamplePatient1.json">
                                    Patient (JSON)
                                </a>
                                <br/>
                                <a rel="noreferrer" target="_blank"
                                   href="http://hl7.org/fhir/us/carin-bb/STU1/ExplanationOfBenefit-EOBInpatient1.json">
                                    ExplanationOfBenefit (JSON)
                                </a>
                                <br/>
                                <a rel="noreferrer" target="_blank"
                                   href="http://hl7.org/fhir/us/carin-bb/STU1/Organization-OrganizationProvider1.json">
                                    OrganizationProvider (JSON)
                                </a>
                                <br/>
                                <a rel="noreferrer" target="_blank"
                                   href="http://hl7.org/fhir/us/davinci-drug-formulary/STU1.0.1/List-CoveragePlanV3001.json">
                                    CoveragePlan (JSON)
                                </a>
                            </Col>
                        </Row>
                    </Modal>
                </Col>
            </Row>
            <Table columns={_columns}
                   pagination={{pageSize: 500, size: "small"}}
                   size="small"
                   components={components}
                   rowClassName={() => 'editable-row'}
                   bordered
                   rowKey="id"
                   dataSource={templates}/>
        </>
    )
}
